import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Ticket, Calendar, MapPin, Scan, UserCircle, LogOut, ChevronRight, Search, X, Shield, LayoutDashboard, Clock, CheckCircle, AlertCircle, Eye, Crown, XCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import axios from 'axios';
import { toast } from 'sonner';
import { formatEventDateTime } from '../utils/dateFormat';
import { getTicketDisplayState, getUserRoleLabel } from '../lib/ticketDisplayState';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Lucide icon registry for the unified display-state helper.
const STATE_ICONS = { CheckCircle, Clock, Eye, AlertCircle, XCircle, Shield };

function getPassStatus(ticket, now) {
  // ── Iter130: thin wrapper around the centralized lifecycle helper.
  //    Returns the same {label, color} shape this file used previously so
  //    callers don't need to change, but resolution now flows through
  //    getTicketDisplayState — which means past-pending tickets render as
  //    "REQUEST EXPIRED" instead of misleadingly "PENDING", etc.
  const s = getTicketDisplayState(ticket, null, now);
  return { label: s.label, color: s.chip, key: s.key, iconName: s.icon };
}

export default function UserDashboard({ user }) {
  const navigate = useNavigate();
  const [tickets, setTickets] = useState([]);
  const [assignedEvents, setAssignedEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('tickets');
  const [search, setSearch] = useState('');
  const [timeFilter, setTimeFilter] = useState('upcoming');

  const fetchData = useCallback(async () => {
    try {
      const token = localStorage.getItem('session_token');
      const headers = { Authorization: `Bearer ${token}` };
      const [ticketsRes, eventsRes] = await Promise.all([
        axios.get(`${API}/users/my-tickets`, { headers }),
        axios.get(`${API}/users/assigned-events`, { headers }).catch(() => ({ data: [] })),
      ]);
      setTickets(ticketsRes.data);
      setAssignedEvents(eventsRes.data);
    } catch {
      toast.error('Failed to load data');
    }
    setLoading(false);
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleLogout = () => {
    localStorage.removeItem('session_token');
    localStorage.removeItem('user');
    navigate('/');
  };

  const now = useMemo(() => new Date(), []);

  const upcomingCount = useMemo(() => tickets.filter(t => {
    if (t.status === 'revoked') return false;
    if (!t.event_date) return true;
    return new Date(t.event_date) >= now;
  }).length, [tickets, now]);

  const pastCount = useMemo(() => tickets.filter(t => {
    if (t.status === 'revoked') return false;
    if (!t.event_date) return false;
    return new Date(t.event_date) < now;
  }).length, [tickets, now]);

  const filteredTickets = useMemo(() => {
    let list = [...tickets];
    if (timeFilter !== 'all') {
      list = list.filter(t => {
        if (!t.event_date) return timeFilter === 'upcoming';
        const eventDate = new Date(t.event_date);
        return timeFilter === 'upcoming' ? eventDate >= now : eventDate < now;
      });
    }
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      list = list.filter(t =>
        (t.event_title || '').toLowerCase().includes(q) ||
        (t.guest_name || '').toLowerCase().includes(q) ||
        (t.tier_name || '').toLowerCase().includes(q)
      );
    }
    return list;
  }, [tickets, timeFilter, search, now]);

  const hasStaffAssignments = assignedEvents.length > 0;

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-[#D4AF37] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] relative" data-testid="user-dashboard">
      {/* ── Iter130: subtle VG monogram watermark — fixed, 1.8% opacity,
            sits behind all content. Desktop-only (hidden on small screens
            to avoid clutter on the limited mobile canvas). No interaction,
            no animation, no extra paint cost. */}
      <div
        aria-hidden="true"
        className="hidden lg:block pointer-events-none fixed inset-0 z-0 select-none"
        style={{
          backgroundImage:
            "radial-gradient(circle at 20% 18%, rgba(212,175,55,0.04) 0px, transparent 40%), radial-gradient(circle at 88% 78%, rgba(198,166,107,0.035) 0px, transparent 38%)",
        }}
      />
      <div
        aria-hidden="true"
        className="hidden lg:block pointer-events-none fixed right-[-80px] bottom-[-60px] z-0 select-none text-[#C6A66B] font-black tracking-[0.18em]"
        style={{ fontSize: '22rem', lineHeight: 1, opacity: 0.018 }}
      >
        VG
      </div>

      {/* Header */}
      <div className="border-b border-[#1A1A1A] relative z-10">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between gap-3">
          <div className="min-w-0">
            <div className="text-[10px] tracking-[3px] text-[#C6A66B] uppercase font-semibold">VelvetGate</div>
            <div className="mt-1 flex items-center gap-2 flex-wrap">
              <div className="text-[#EAE7E1] font-semibold truncate" data-testid="user-display-name">{user?.screen_name || user?.name || 'Guest'}</div>
              {/* ── Iter130: subtle role pill. Muted, premium, never dominates. */}
              {(() => {
                const r = getUserRoleLabel(user?.role);
                return (
                  <span
                    className={`inline-flex items-center px-2 py-[2px] rounded-sm border text-[9px] font-bold uppercase tracking-[0.18em] ${r.cls}`}
                    data-testid={`user-role-pill-${user?.role || 'user'}`}
                  >
                    {r.label}
                  </span>
                );
              })()}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {user?.role === 'admin' && (
              <Button variant="outline" size="sm" onClick={() => navigate('/admin')} className="border-[#1A1A1A] text-[#AFAFAF] rounded-sm text-xs" data-testid="admin-dashboard-link">
                <Shield className="w-3.5 h-3.5 mr-1" />Admin
              </Button>
            )}
            {(user?.role === 'host' || user?.role === 'admin') && (
              <Button variant="outline" size="sm" onClick={() => navigate('/dashboard')} className="border-[#1A1A1A] text-[#AFAFAF] rounded-sm text-xs" data-testid="host-dashboard-link">
                <LayoutDashboard className="w-3.5 h-3.5 mr-1" />Host
              </Button>
            )}
            <Button variant="outline" size="sm" onClick={() => navigate('/calendar')} className="border-[#1A1A1A] text-[#AFAFAF] rounded-sm" data-testid="calendar-button">
              <Calendar className="w-4 h-4" />
            </Button>
            <Button variant="outline" size="sm" onClick={() => navigate('/account')} className="border-[#1A1A1A] text-[#AFAFAF] rounded-sm" data-testid="account-button">
              <UserCircle className="w-4 h-4" />
            </Button>
            <Button variant="outline" size="sm" onClick={handleLogout} className="border-[#1A1A1A] text-[#AFAFAF] rounded-sm" data-testid="logout-button">
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-6 relative z-10">
        {/* Host application CTA — only for regular users (hosts/admins have their own dashboard) */}
        {user?.role === 'user' && (
          <button
            onClick={() => navigate('/account')}
            className="w-full flex items-center justify-between gap-3 bg-[#0A0A0A] border border-[#D4AF37]/25 hover:border-[#D4AF37]/50 rounded-sm p-4 mb-4 transition-colors text-left"
            data-testid="apply-host-cta"
          >
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-9 h-9 rounded-full bg-[#D4AF37]/10 flex items-center justify-center flex-shrink-0">
                <Crown className="w-4 h-4 text-[#D4AF37]" />
              </div>
              <div className="min-w-0">
                <div className="text-sm font-semibold text-[#EAE7E1]">Want to host your own events?</div>
                <div className="text-xs text-[#AFAFAF] mt-0.5">Apply to host — requires approval. Staff check-in roles are invited by a host.</div>
              </div>
            </div>
            <ChevronRight className="w-4 h-4 text-[#D4AF37] flex-shrink-0" />
          </button>
        )}

        {/* Tabs */}
        <div className="flex gap-1 mb-4 border-b border-[#1A1A1A]">
          <button onClick={() => setTab('tickets')} className={`px-4 py-2.5 text-sm font-medium transition-colors ${tab === 'tickets' ? 'text-[#D4AF37] border-b-2 border-[#D4AF37]' : 'text-[#777] hover:text-[#AFAFAF]'}`} data-testid="tab-tickets">
            <Ticket className="w-4 h-4 inline mr-2" />My Passes ({tickets.length})
          </button>
          {hasStaffAssignments && (
            <button onClick={() => setTab('staff')} className={`px-4 py-2.5 text-sm font-medium transition-colors ${tab === 'staff' ? 'text-[#D4AF37] border-b-2 border-[#D4AF37]' : 'text-[#777] hover:text-[#AFAFAF]'}`} data-testid="tab-staff">
              <Scan className="w-4 h-4 inline mr-2" />Staff ({assignedEvents.length})
            </button>
          )}
        </div>

        {/* My Passes */}
        {tab === 'tickets' && (
          <div data-testid="tickets-section">
            {/* Stats summary */}
            {tickets.length > 0 && (
              <div className="flex items-center gap-4 mb-4 text-xs" data-testid="pass-stats">
                <span className="text-emerald-400 font-medium" data-testid="upcoming-count">{upcomingCount} upcoming</span>
                <span className="text-[#555]">&middot;</span>
                <span className="text-[#777]" data-testid="past-count">{pastCount} past</span>
              </div>
            )}

            {/* Filters */}
            {tickets.length > 0 && (
              <div className="flex flex-col sm:flex-row gap-3 mb-5" data-testid="ticket-filters">
                <div className="flex gap-1">
                  {[
                    { key: 'upcoming', label: 'Upcoming', count: upcomingCount },
                    { key: 'past', label: 'Past', count: pastCount },
                    { key: 'all', label: 'All', count: tickets.length }
                  ].map(f => (
                    <button
                      key={f.key}
                      onClick={() => setTimeFilter(f.key)}
                      className={`px-3 py-1.5 text-xs font-medium rounded-sm transition-colors ${timeFilter === f.key ? 'bg-[#D4AF37]/15 text-[#D4AF37] border border-[#D4AF37]/30' : 'text-[#777] border border-[#1A1A1A] hover:border-[#333] hover:text-[#AFAFAF]'}`}
                      data-testid={`filter-${f.key}`}
                    >
                      {f.label} ({f.count})
                    </button>
                  ))}
                </div>
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#555]" />
                  <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search events..." className="pl-9 h-8 bg-[#0A0A0A] border-[#1A1A1A] text-[#EAE7E1] placeholder:text-[#555] rounded-sm text-sm" data-testid="ticket-search" />
                  {search && (
                    <button onClick={() => setSearch('')} className="absolute right-2 top-1/2 -translate-y-1/2 text-[#555] hover:text-[#AFAFAF]">
                      <X className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              </div>
            )}

            {/* Pass cards */}
            <div className="space-y-3" data-testid="tickets-list">
              {tickets.length === 0 ? (
                <div className="text-center py-16">
                  <Ticket className="w-12 h-12 text-[#333] mx-auto mb-3" />
                  <div className="text-[#777] text-sm">No passes yet</div>
                  <div className="text-[#555] text-xs mt-1">Passes you purchase will appear here</div>
                </div>
              ) : filteredTickets.length === 0 ? (
                <div className="text-center py-12">
                  <div className="text-[#777] text-sm">No matching passes</div>
                  <div className="text-[#555] text-xs mt-1">
                    {search ? 'Try a different search' : timeFilter === 'upcoming' ? 'No upcoming events' : 'No past events'}
                  </div>
                </div>
              ) : (
                filteredTickets.map(t => {
                  const passStatus = getPassStatus(t, now);
                  const isActive = passStatus.key === 'granted';
                  const isRevokedCard = t.status === 'revoked';
                  const StatusIcon = STATE_ICONS[passStatus.iconName] || CheckCircle;
                  return (
                    <div
                      key={t.ticket_id}
                      className={`${isRevokedCard ? 'bg-[#0A0A0A] border-2 border-[#C44545]/40 opacity-75' : 'bg-[#0A0A0A] border rounded-sm'} overflow-hidden rounded-sm transition-all ${!isRevokedCard && (isActive ? 'border-[#1A1A1A] hover:border-[#D4AF37]/30 sm:hover:translate-y-[-1px] sm:hover:shadow-[0_4px_24px_rgba(212,175,55,0.06)]' : 'border-[#1A1A1A] hover:border-[#333]')}`}
                      data-testid={`pass-card-${t.ticket_id}`}
                    >
                      {/* Card top accent */}
                      {isActive && <div className="h-0.5 bg-gradient-to-r from-[#8C6B1F] via-[#D4AF37] to-[#F2D27A]" />}
                      {isRevokedCard && <div className="h-0.5 bg-[#C44545]" />}

                      <div className="p-4 sm:p-5">
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-2 flex-wrap">
                              <span
                                className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-[0.12em] px-2 py-0.5 rounded border ${passStatus.color}`}
                                data-testid={`pass-status-${t.ticket_id}`}
                                data-pass-state={passStatus.key}
                              >
                                <StatusIcon className="w-2.5 h-2.5" />
                                {passStatus.label}
                              </span>
                              <span className="text-[10px] text-[#555] font-medium uppercase tracking-wider">{t.tier_name || 'General'}</span>
                            </div>
                            <h3 className="text-sm sm:text-base font-semibold text-[#EAE7E1] leading-tight">{t.event_title}</h3>
                            {t.replaces_ticket_id && (
                              <div className="mt-1 inline-flex items-center gap-1 text-[9px] font-bold text-[#D4AF37] uppercase tracking-wider" data-testid={`replacement-chip-${t.ticket_id}`}>Replacement pass</div>
                            )}
                            {t.replaced_by_ticket_id && (
                              <div className="mt-1 inline-flex items-center gap-1 text-[9px] font-bold text-[#C44545] uppercase tracking-wider" data-testid={`replaced-chip-${t.ticket_id}`}>Replaced — see newer pass</div>
                            )}
                            <div className="mt-2 space-y-0.5">
                              <div className="text-xs text-[#777] flex items-center gap-1.5">
                                <Calendar className="w-3 h-3 flex-shrink-0" />
                                <span>{formatEventDateTime(t.event_date, t.event_time)}</span>
                              </div>
                              {t.event_location && (
                                <div className="text-xs text-[#555] flex items-center gap-1.5">
                                  <MapPin className="w-3 h-3 flex-shrink-0" />
                                  <span className="truncate">{t.event_location}</span>
                                </div>
                              )}
                            </div>
                          </div>
                          <Button
                            size="sm"
                            onClick={(e) => { e.stopPropagation(); navigate(`/ticket/${t.ticket_id}`); }}
                            className={`flex-shrink-0 rounded-sm text-xs font-semibold h-8 ${isActive ? 'bg-[#D4AF37] hover:bg-[#F2D27A] text-[#050505]' : 'bg-[#1A1A1A] hover:bg-[#222] text-[#AFAFAF]'}`}
                            data-testid={`view-pass-${t.ticket_id}`}
                          >
                            View Pass <ChevronRight className="w-3 h-3 ml-1" />
                          </Button>
                        </div>
                        {t.rsvp_status === 'rsvp' && t.status !== 'used' && t.status !== 'revoked' && (
                          <div className="mt-3 flex justify-end">
                            <button
                              type="button"
                              onClick={async (e) => {
                                e.stopPropagation();
                                if (!window.confirm('Cancel your RSVP for this event?')) return;
                                try {
                                  const token = localStorage.getItem('session_token');
                                  await axios.post(`${API}/tickets/${t.ticket_id}/cancel`, {}, { headers: { Authorization: `Bearer ${token}` } });
                                  toast.success('RSVP cancelled');
                                  fetchData();
                                } catch (err) {
                                  toast.error(err.response?.data?.detail || 'Failed to cancel');
                                }
                              }}
                              className="text-xs text-[#C44545] hover:text-[#E25555] underline"
                              data-testid={`cancel-rsvp-${t.ticket_id}`}
                            >
                              Cancel RSVP
                            </button>
                          </div>
                        )}
                        {t.rsvp_status === 'cancelled' && (
                          <div className="mt-2 text-xs text-[#C44545] font-semibold uppercase tracking-wider" data-testid={`cancelled-chip-${t.ticket_id}`}>RSVP cancelled</div>
                        )}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        )}

        {/* Assigned Events (Staff) */}
        {tab === 'staff' && hasStaffAssignments && (
          <div className="space-y-3" data-testid="assigned-events-list">
            {assignedEvents.map(e => (
              <div key={e.event_id} className="bg-[#0A0A0A] border border-[#1A1A1A] rounded-sm p-4 cursor-pointer hover:border-[#333] transition-colors" onClick={() => navigate(`/events/${e.event_id}/check-in`)} data-testid={`staff-event-${e.event_id}`}>
                <div className="flex items-center justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-[#EAE7E1]">{e.title}</div>
                    <div className="text-xs text-[#777] mt-1"><Calendar className="w-3 h-3 inline mr-1" />{formatEventDateTime(e.date, e.time)}</div>
                    {e.location && <div className="text-xs text-[#555] mt-0.5"><MapPin className="w-3 h-3 inline mr-1" />{e.location}</div>}
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <span className="text-[9px] font-bold uppercase px-1.5 py-0.5 rounded bg-[#C6A66B]/15 text-[#C6A66B]">Staff</span>
                    <ChevronRight className="w-4 h-4 text-[#333]" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
